/**
 * StripeInvoices.jsx – Phase 1a: Rechnungsliste (Read-Only)
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Download, ExternalLink, Filter, RefreshCw, XCircle, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_OPTIONS = [
  { value: '', label: 'Alle Status' },
  { value: 'paid', label: 'Bezahlt' },
  { value: 'open', label: 'Offen' },
  { value: 'draft', label: 'Entwurf' },
  { value: 'void', label: 'Storniert' },
  { value: 'uncollectible', label: 'Uneinbringlich' },
];

function InvStatusBadge({ status, statusDe }) {
  const c = {
    paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    open: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800',
    void: 'bg-gray-100 text-gray-500',
    uncollectible: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c}`}>{statusDe || status}</span>;
}

export default function StripeInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [startingAfter, setStartingAfter] = useState(null);
  const [error, setError] = useState(null);

  const load = async (after = null, st = status) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: 30 });
      if (st) params.set('status', st);
      if (after) params.set('starting_after', after);
      const res = await axios.get(`${API}/api/superadmin/stripe-cc/invoices?${params}`, { headers: authHeader() });
      if (after) {
        setInvoices(prev => [...prev, ...(res.data.invoices || [])]);
      } else {
        setInvoices(res.data.invoices || []);
      }
      setHasMore(res.data.has_more || false);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fehler beim Laden');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(null, status); setStartingAfter(null); }, [status]); // eslint-disable-line

  const loadMore = () => {
    const lastId = invoices[invoices.length - 1]?.id;
    if (lastId) { setStartingAfter(lastId); load(lastId); }
  };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            className="pl-8 pr-8 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none appearance-none"
            data-testid="stripe-invoices-filter"
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        </div>
        <Button variant="outline" size="sm" onClick={() => load(null, status)} disabled={loading} className="gap-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aktualisieren
        </Button>
        <span className="text-xs text-muted-foreground">{invoices.length} Rechnungen geladen</span>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 rounded-xl text-sm text-red-700">
          <XCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Tabelle */}
      {loading && invoices.length === 0 ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              <span>Kunde</span>
              <span className="hidden sm:block">Betrag</span>
              <span className="hidden sm:block">Datum</span>
              <span>Status</span>
              <span></span>
            </div>
            {/* Rows */}
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {invoices.map(inv => (
                <div key={inv.id} className="grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 px-4 py-3 items-center hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{inv.customer_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{inv.number || inv.id?.slice(-8)}</p>
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-semibold">{inv.amount_due_display}</p>
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-xs text-muted-foreground">{inv.date}</p>
                    {inv.due_date && inv.status !== 'paid' && (
                      <p className="text-xs text-amber-600">Fällig: {inv.due_date}</p>
                    )}
                  </div>
                  <div>
                    <InvStatusBadge status={inv.status} statusDe={inv.status_de} />
                  </div>
                  <div className="flex items-center gap-1">
                    {inv.pdf_url && (
                      <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
                        title="PDF herunterladen">
                        <Download className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {inv.hosted_url && (
                      <a href={inv.hosted_url} target="_blank" rel="noreferrer"
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
                        title="In Stripe öffnen">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {invoices.length === 0 && !loading && (
                <p className="text-center py-12 text-sm text-muted-foreground">Keine Rechnungen gefunden</p>
              )}
            </div>
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={loadMore} disabled={loading} className="gap-1.5">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Mehr laden
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
